import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { HorizontalAlign } from "@pdfking/renderer-api"
import { Range } from "@pdfking/tfunc"

import { PALETTE, WEEK_ROWS } from "#constants"
import type { CalendarArea } from "#internal"

import { Slice } from "../layout/Slice"
import { Area } from "./Area"
import { DayColor } from "./DayColor"
import { MonthName } from "./MonthName"
import { WeekdayFont } from "./WeekdayFont"
import { WeekdayNames } from "./WeekdayNames"

/** Months `first` to `last` of `year` across `box` a column each, their days
 * on the rows of their weekdays, the weekdays' initials down its left, a rule
 * under each row across them all to write on, each month its name over it
 * `title` tall: the whole year unless told; their names in `language`. */
export const YearColumns = ({
  box,
  first = 1,
  gap,
  language,
  last = 12,
  title,
  year,
}: {
  box: SpatialRectangle<Distance>
  first?: number
  gap: Distance
  language: string
  last?: number
  title: Distance
  year: number
}): CalendarArea[] => {
  // The weekdays' column, then the months': each its title over its days
  const columns = Slice.grid(box, last - first + 2, 1, ZERO_DISTANCE).map(column =>
    Slice.top(column, title, gap)
  )
  const days = columns[0]![1]
  const row = Scale(days.height, 1 / WEEK_ROWS)
  const font = WeekdayFont(Scale(row, 0.45))
  const initials = WeekdayNames(language).map(name => name.slice(0, 1).toUpperCase())
  return [
    // A rule under each row, across the weekdays and the months
    Area.rules({ ...days, width: box.width }, row),
    ...[...Range(WEEK_ROWS)].map(i =>
      Area.text(
        {
          ...days,
          height: font,
          y: Total(
            Subtract(Total(days.y, days.height), Scale(row, i + 1)),
            Scale(Subtract(row, font), 0.5)
          ),
        },
        initials[i % 7]!,
        DayColor(i % 7),
        HorizontalAlign.CENTER
      )
    ),
    ...columns.slice(1).flatMap(([name, column], i) => [
      Area.text(
        name,
        MonthName(first + i, language)
          .slice(0, 3)
          .toUpperCase(),
        PALETTE.secondary,
        HorizontalAlign.CENTER
      ),
      Area.weeks(column, first + i, year),
    ]),
  ]
}
